// v10/src/components/PaymentResult.js
// v1.1 — Fix i18n: PLAN_FEATURES_UNLOCKED movido a función getPlanFeaturesUnlocked(t) para evitar t() a nivel de módulo
// Overlay de resultado de pago — success / failure / pending
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ArrowRight, RotateCcw, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PLAN_LABELS = { basic: 'Basic', pro: 'Pro', enterprise: 'Enterprise' };

const getPlanFeaturesUnlocked = (t) => ({
  basic: [t('payment_basic_fleet',{ns:'admin'}), t('payment_basic_users',{ns:'admin'}), t('payment_basic_hobbs',{ns:'admin'}), t('payment_basic_reports',{ns:'admin'})],
  pro: ['Checklist pre-aterrizaje', 'Voz Valentina premium', 'Firma digital', 'Squawks avanzados', 'Libro de vuelos', 'Proveedores'],
});

const Confetti = () => {
  const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6'];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {Array.from({ length: 40 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -20, x: Math.random() * 100 + '%', opacity: 1, rotate: 0 }}
          animate={{ y: '110vh', opacity: 0, rotate: Math.random() * 720 - 360 }}
          transition={{ duration: 2 + Math.random() * 2, delay: Math.random() * 0.8, ease: 'easeIn' }}
          style={{
            position: 'absolute', width: 8 + Math.random() * 6, height: 8 + Math.random() * 6,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            background: colors[Math.floor(Math.random() * colors.length)],
          }}
        />
      ))}
    </div>
  );
};

const PaymentResult = ({ status, plan, onClose }) => {
  const { t } = useTranslation();
  const PLAN_FEATURES_UNLOCKED = getPlanFeaturesUnlocked(t);

  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Limpiar query params de la URL
    const url = new URL(window.location.href);
    url.searchParams.delete('payment');
    url.searchParams.delete('plan');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose && onClose(), 300);
  };

  const handleRetry = () => {
    setVisible(false);
    setTimeout(() => onClose && onClose(), 300);
  };

  if (!visible) return null;

  const planLabel = PLAN_LABELS[plan] || plan || '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
      >
        {/* Backdrop */}
        <div style={{
          position: 'absolute', inset: 0,
          background: status === 'success' ? 'rgba(5,46,22,0.95)'
            : status === 'failure' ? 'rgba(69,10,10,0.95)'
            : 'rgba(66,32,6,0.95)',
          backdropFilter: 'blur(12px)',
        }} />

        {status === 'success' && <Confetti />}

        <motion.div
          initial={{ scale: 0.8, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          style={{
            position: 'relative', zIndex: 1, width: '100%', maxWidth: 420,
            borderRadius: 28, padding: 40,
            background: status === 'success' ? 'rgba(34,197,94,0.1)'
              : status === 'failure' ? 'rgba(239,68,68,0.1)'
              : 'rgba(234,179,8,0.1)',
            border: `1px solid ${status === 'success' ? 'rgba(34,197,94,0.2)' : status === 'failure' ? 'rgba(239,68,68,0.2)' : 'rgba(234,179,8,0.2)'}`,
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2, damping: 12 }}
            style={{ marginBottom: 20 }}
          >
            {status === 'success' && (
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                <CheckCircle style={{ width: 44, height: 44, color: '#22c55e' }} />
              </div>
            )}
            {status === 'failure' && (
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                <XCircle style={{ width: 44, height: 44, color: '#ef4444' }} />
              </div>
            )}
            {status === 'pending' && (
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                <Clock style={{ width: 44, height: 44, color: '#eab308' }} />
              </div>
            )}
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 900, color: 'white' }}
          >
            {status === 'success' && '¡Suscripción activada!'}
            {status === 'failure' && 'El pago no se pudo procesar'}
            {status === 'pending' && 'Pago en proceso'}
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{ margin: '0 0 24px', fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}
          >
            {status === 'success' && `Tu plan ${planLabel} está activo.`}
            {status === 'failure' && 'No se realizó ningún cobro.'}
            {status === 'pending' && 'Te avisamos cuando se confirme.'}
          </motion.p>

          {/* Features (success) */}
          {status === 'success' && PLAN_FEATURES_UNLOCKED[plan] && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{
                marginBottom: 24, padding: '16px 20px', borderRadius: 16,
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)',
                textAlign: 'left',
              }}
            >
              <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Features desbloqueadas
              </p>
              {PLAN_FEATURES_UNLOCKED[plan].map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                  <CheckCircle style={{ width: 13, height: 13, color: '#22c55e', flexShrink: 0 }} />
                  {f}
                </div>
              ))}
            </motion.div>
          )}

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            {status === 'success' && (
              <button onClick={handleClose}
                style={{
                  width: '100%', padding: '14px', borderRadius: 14, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white',
                  fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                <ArrowRight style={{ width: 18, height: 18 }} />
                Ir al panel
              </button>
            )}

            {status === 'failure' && (
              <>
                <button onClick={handleRetry}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white',
                    fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  <RotateCcw style={{ width: 16, height: 16 }} />
                  Intentar de nuevo
                </button>
                <button onClick={handleClose}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 14,
                    border: '1px solid rgba(255,255,255,0.15)', background: 'transparent',
                    color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: 14,
                    fontFamily: 'inherit', cursor: 'pointer',
                  }}>
                  Volver al panel
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                  <Mail style={{ width: 12, height: 12 }} />
                  info@condor360.app
                </div>
              </>
            )}

            {status === 'pending' && (
              <button onClick={handleClose}
                style={{
                  width: '100%', padding: '14px', borderRadius: 14, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #eab308, #ca8a04)', color: 'white',
                  fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                <ArrowRight style={{ width: 18, height: 18 }} />
                Volver al panel
              </button>
            )}
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentResult;
